import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier si l'utilisateur est connecté au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Timeout de 5s pour ne pas bloquer si backend down
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('TIMEOUT')), 5000)
          );
          const response = await Promise.race([
            authAPI.getProfile(),
            timeoutPromise
          ]);

          setUser(response.data.user);
          connectSocket(token);
        } catch (err) {
          // Si timeout ou erreur réseau, on garde le user du localStorage
          // (affichage immédiat) MAIS on connecte quand même le socket et on
          // reprogramme un vrai rafraîchissement — sinon le solde reste figé
          // indéfiniment (cas typique: reprise de l'app iOS depuis l'écran
          // d'accueil avec le réseau pas encore rétabli).
          if (err.message === 'TIMEOUT' || err.code === 'ERR_NETWORK' || !err.response) {
            const cachedUser = localStorage.getItem('user');
            if (cachedUser) {
              try {
                setUser(JSON.parse(cachedUser));
                connectSocket(token);
                setTimeout(() => refreshUser(), 3000);
              } catch (e) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
              }
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Rafraîchir le solde/profil quand l'app redevient active (retour au
  // premier plan, écran verrouillé/déverrouillé, changement réseau) — sans
  // ça, une app iOS relancée depuis l'écran d'accueil peut rester figée sur
  // un solde périmé jusqu'au prochain rechargement complet.
  useEffect(() => {
    const onResume = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
        refreshUser();
      }
    };
    const onOnline = () => {
      if (localStorage.getItem('token')) refreshUser();
    };

    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // Ouvre la session (token + user) — partagé par login, et par la
  // vérification d'email qui complète soit une inscription soit une connexion.
  const setSession = (token, freshUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(freshUser));
    setUser(freshUser);
    connectSocket(token);
  };

  // Inscription — n'ouvre PAS de session: un code de vérification est envoyé
  // par email, la session ne démarre qu'après verifyEmail() réussi.
  const register = async (username, email, password, referralCode = '') => {
    try {
      setError(null);
      const data = { username, email, password };
      if (referralCode) {
        data.referralCode = referralCode;
      }
      const response = await authAPI.register(data);
      return { success: true, requiresEmailVerification: true, email: response.data.email };
    } catch (err) {
      const message = err.response?.data?.error || 'Erreur lors de l\'inscription';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Connexion — si le compte n'est pas encore vérifié, aucune session n'est
  // ouverte: un nouveau code est envoyé et l'appelant doit rediriger vers
  // l'écran de vérification.
  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data;

      setSession(token, user);

      return { success: true, user };
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresEmailVerification) {
        return { success: false, requiresEmailVerification: true, email: data.email, error: data.error };
      }
      if (data?.requiresAdminCode) {
        return { success: false, requiresAdminCode: true, email: data.email, error: data.error };
      }
      const message = data?.error || 'Identifiants incorrects';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Valide le code 2FA admin/manager reçu après un mot de passe correct —
  // ouvre la session (voir setSession).
  const verifyAdminLogin = async (email, code) => {
    try {
      setError(null);
      const response = await authAPI.verifyAdminLogin(email, code);
      const { token, user } = response.data;
      setSession(token, user);
      return { success: true, user };
    } catch (err) {
      const data = err.response?.data;
      return {
        success: false,
        error: data?.error || 'Code invalide',
        expired: data?.expired,
        maxAttemptsReached: data?.maxAttemptsReached
      };
    }
  };

  // Redemande un code 2FA admin (respecte le cooldown anti-spam serveur)
  const resendAdminCode = async (email) => {
    try {
      const response = await authAPI.resendAdminCode(email);
      return { success: true, message: response.data.message };
    } catch (err) {
      const data = err.response?.data;
      return { success: false, error: data?.error, retryAfterSeconds: data?.retryAfterSeconds };
    }
  };

  // Valide le code reçu par email — complète l'inscription ou la connexion
  // en ouvrant la session (voir setSession).
  const verifyEmail = async (email, code) => {
    try {
      setError(null);
      const response = await authAPI.verifyEmail(email, code);
      const { token, user } = response.data;
      setSession(token, user);
      return { success: true, user };
    } catch (err) {
      const data = err.response?.data;
      return {
        success: false,
        error: data?.error || 'Code invalide',
        expired: data?.expired,
        maxAttemptsReached: data?.maxAttemptsReached
      };
    }
  };

  // Redemande un code de vérification (respecte le cooldown anti-spam serveur)
  const resendVerification = async (email) => {
    try {
      const response = await authAPI.resendVerification(email);
      return { success: true, message: response.data.message };
    } catch (err) {
      const data = err.response?.data;
      return { success: false, error: data?.error, retryAfterSeconds: data?.retryAfterSeconds };
    }
  };

  // Demande un code de réinitialisation de mot de passe par email
  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email);
      return { success: true, message: response.data.message };
    } catch (err) {
      const data = err.response?.data;
      return { success: false, error: data?.error, retryAfterSeconds: data?.retryAfterSeconds };
    }
  };

  // Valide le code et applique le nouveau mot de passe (n'ouvre pas de
  // session — l'utilisateur se reconnecte normalement ensuite)
  const resetPassword = async (email, code, newPassword) => {
    try {
      const response = await authAPI.resetPassword(email, code, newPassword);
      return { success: true, message: response.data.message };
    } catch (err) {
      const data = err.response?.data;
      return {
        success: false,
        error: data?.error || 'Code invalide',
        expired: data?.expired,
        maxAttemptsReached: data?.maxAttemptsReached
      };
    }
  };

  // Déconnexion
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    disconnectSocket();
  };

  // Recharger le profil depuis la DB (à appeler après toute action qui modifie l'utilisateur)
  const refreshUser = async () => {
    try {
      const response = await authAPI.getProfile();
      const freshUser = response.data.user;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (err) {
      // Silencieux - le token est peut-être expiré, l'intercepteur 401 gère ce cas
    }
  };

  // Mettre à jour le profil
  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Erreur lors de la mise à jour';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateProfile,
    refreshUser,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    verifyAdminLogin,
    resendAdminCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
